import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimeGrid } from '@/components/anime/AnimeGrid';
import {
	FilterBar,
	type BrowseFilterValues,
} from '@/components/anime/FilterBar';
import { Pagination } from '@/components/ui/Pagination';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { DEFAULT_PAGE_SIZE, SORT_OPTIONS } from '@/lib/constants';
import { useAnimeList, useCountries, useGenres } from '@/hooks/useAnimeQueries';
import type { SortField, SortLang, SortType } from '@/types/anime';

const DEFAULT_SORT = 'modified.time-desc';

/** "year-desc" → field/type đã kiểm tra; URL cũ `view-desc` map sang `_id`. */
function parseSort(value: string): { field: SortField; type: SortType } {
	const normalized = value === 'view-desc' ? '_id-desc' : value;
	const match = SORT_OPTIONS.find(
		(option) => `${option.sortField}-${option.sortType}` === normalized,
	);
	return {
		field: match?.sortField ?? 'modified.time',
		type: match?.sortType ?? 'desc',
	};
}

const VALID_LANGS: SortLang[] = ['', 'vietsub', 'thuyet-minh', 'long-tieng'];

/**
 * Trang Duyệt: toàn bộ trạng thái lọc nằm trong URL nên có thể chia sẻ link,
 * và nút back/forward của trình duyệt hoạt động đúng.
 */
export function BrowsePage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const genresQuery = useGenres();
	const countriesQuery = useCountries();

	const rawLang = searchParams.get('lang') ?? '';
	const values: BrowseFilterValues = {
		category: searchParams.get('category') ?? '',
		country: searchParams.get('country') ?? '',
		year: searchParams.get('year') ?? '',
		sort: searchParams.get('sort') ?? DEFAULT_SORT,
		lang: (VALID_LANGS as string[]).includes(rawLang)
			? (rawLang as SortLang)
			: '',
	};
	const page = Math.max(1, Number(searchParams.get('page')) || 1);
	const sort = parseSort(values.sort);

	const filters = useMemo(
		() => ({
			page,
			limit: DEFAULT_PAGE_SIZE,
			sortField: sort.field,
			sortType: sort.type,
			sortLang: values.lang,
			category: values.category,
			country: values.country,
			year: values.year,
		}),
		[
			page,
			sort.field,
			sort.type,
			values.lang,
			values.category,
			values.country,
			values.year,
		],
	);

	const listQuery = useAnimeList(filters);

	const updateParams = (
		patch: Record<string, string>,
		options?: { keepPage?: boolean },
	) => {
		const next = new URLSearchParams(searchParams);
		for (const [key, value] of Object.entries(patch)) {
			if (value) next.set(key, value);
			else next.delete(key);
		}
		// Đổi bộ lọc thì phải về trang 1, nếu không sẽ rơi vào trang không tồn tại.
		if (!options?.keepPage) next.delete('page');
		setSearchParams(next);
	};

	const activeGenre = genresQuery.data?.find(
		(genre) => genre.slug === values.category,
	);
	const activeCountry = countriesQuery.data?.find(
		(country) => country.slug === values.country,
	);

	const heading =
		[
			'Hoạt hình',
			activeGenre?.name,
			activeCountry?.name,
			values.year || undefined,
		]
			.filter(Boolean)
			.join(' · ') || 'Hoạt hình';

	return (
		<section className="mt-6">
			<SectionHeader
				title={heading}
				action={
					listQuery.data && (
						<span className="text-[13px] text-muted">
							{listQuery.data.pagination.totalItems.toLocaleString('vi-VN')} kết
							quả
						</span>
					)
				}
			/>

			<FilterBar
				values={values}
				genres={genresQuery.data ?? []}
				countries={countriesQuery.data ?? []}
				onChange={(patch) =>
					updateParams({
						category: patch.category ?? values.category,
						country: patch.country ?? values.country,
						year: patch.year ?? values.year,
						lang: patch.lang ?? values.lang,
						sort:
							(patch.sort ?? values.sort) === DEFAULT_SORT
								? ''
								: (patch.sort ?? values.sort),
					})
				}
				onReset={() => setSearchParams(new URLSearchParams())}
			/>

			<AnimeGrid
				items={listQuery.data?.items ?? []}
				isLoading={listQuery.isPending}
				isRefreshing={listQuery.isFetching && !listQuery.isPending}
				error={listQuery.error}
				onRetry={() => void listQuery.refetch()}
				skeletonCount={DEFAULT_PAGE_SIZE}
				emptyTitle="Không có anime nào khớp bộ lọc"
				emptyDescription="Bỏ bớt một vài điều kiện rồi thử lại nhé."
			/>

			{listQuery.data && (
				<Pagination
					pagination={listQuery.data.pagination}
					disabled={listQuery.isFetching}
					onPageChange={(nextPage) =>
						updateParams({ page: String(nextPage) }, { keepPage: true })
					}
				/>
			)}
		</section>
	);
}
